import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../../api/client';
import styles from './StudentDetailPage.module.css';

interface StudentDetail {
  id: string;
  firstName: string;
  paternalLastName: string;
  curp: string;
  birthDate: string;
  bloodType: string;
  allergies: string;
  emergencyContact: string;
  createdAt: string;
  enrollments: {
    id: string;
    status: string;
    year: string;
    group?: { name: string; grade?: { name: string } };
  }[];
  attendance: { id: string; date: string; type: string; method: string }[];
  academicRecords: { id: string; subject: string; grade: number; period: string }[];
  payments: { id: string; total: number; status: string; period: string; fee: { name: string } }[];
}

export default function StudentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [student, setStudent] = useState<StudentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'info' | 'attendance' | 'grades' | 'payments'>('info');

  useEffect(() => {
    if (!id) return;
    api
      .get<any>(`/api/students/${id}`)
      .then((data) => {
        setStudent(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  if (loading)
    return (
      <div className={styles.container}>
        <p>Cargando...</p>
      </div>
    );
  if (!student)
    return (
      <div className={styles.container}>
        <p>Alumno no encontrado</p>
      </div>
    );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button onClick={() => navigate('/dashboard/students')} className={styles.backBtn}>
          ← Volver
        </button>
        <h1>
          {student.firstName} {student.paternalLastName}
        </h1>
        <span className={styles.badge}>
          {student.enrollments?.[0]?.status || 'Sin inscripción'}
        </span>
      </div>

      <div className={styles.infoBar}>
        <div>
          <strong>CURP:</strong> {student.curp || 'N/A'}
        </div>
        <div>
          <strong>Grado:</strong> {student.enrollments?.[0]?.group?.grade?.name || 'N/A'}
        </div>
        <div>
          <strong>Grupo:</strong> {student.enrollments?.[0]?.group?.name || 'N/A'}
        </div>
        <div>
          <strong>Ciclo:</strong> {student.enrollments?.[0]?.year || 'N/A'}
        </div>
      </div>

      <div className={styles.tabs}>
        {(['info', 'attendance', 'grades', 'payments'] as const).map((t) => (
          <button
            key={t}
            className={`${styles.tab} ${tab === t ? styles.active : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'info'
              ? 'Datos Personales'
              : t === 'attendance'
                ? 'Asistencia'
                : t === 'grades'
                  ? 'Calificaciones'
                  : 'Pagos'}
          </button>
        ))}
      </div>

      {tab === 'info' && (
        <div className={styles.card}>
          <div className={styles.field}>
            <label>Nombre(s)</label>
            <span>{student.firstName}</span>
          </div>
          <div className={styles.field}>
            <label>Apellidos</label>
            <span>{student.paternalLastName}</span>
          </div>
          <div className={styles.field}>
            <label>CURP</label>
            <span>{student.curp || 'N/A'}</span>
          </div>
          <div className={styles.field}>
            <label>Fecha de Nacimiento</label>
            <span>
              {student.birthDate ? new Date(student.birthDate).toLocaleDateString() : 'N/A'}
            </span>
          </div>
          <div className={styles.field}>
            <label>Tipo de Sangre</label>
            <span>{student.bloodType || 'N/A'}</span>
          </div>
          <div className={styles.field}>
            <label>Alergias</label>
            <span>{student.allergies || 'Ninguna'}</span>
          </div>
          <div className={styles.field}>
            <label>Contacto de Emergencia</label>
            <span>{student.emergencyContact || 'N/A'}</span>
          </div>
          <div className={styles.field}>
            <label>Registrado</label>
            <span>{new Date(student.createdAt).toLocaleDateString()}</span>
          </div>
        </div>
      )}

      {tab === 'attendance' && (
        <div className={styles.card} style={{ gridTemplateColumns: '1fr' }}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tipo</th>
                <th>Método</th>
              </tr>
            </thead>
            <tbody>
              {student.attendance.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center' }}>
                    Sin registros
                  </td>
                </tr>
              ) : (
                student.attendance.map((a) => (
                  <tr key={a.id}>
                    <td>{new Date(a.date).toLocaleString()}</td>
                    <td>
                      <span className={a.type === 'ENTRY' ? styles.entry : styles.exit}>
                        {a.type === 'ENTRY' ? 'Entrada' : 'Salida'}
                      </span>
                    </td>
                    <td>{a.method}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'grades' && (
        <div className={styles.card} style={{ gridTemplateColumns: '1fr' }}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Materia</th>
                <th>Periodo</th>
                <th>Calificación</th>
              </tr>
            </thead>
            <tbody>
              {student.academicRecords.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center' }}>
                    Sin registros
                  </td>
                </tr>
              ) : (
                student.academicRecords.map((r) => (
                  <tr key={r.id}>
                    <td>{r.subject}</td>
                    <td>{r.period}</td>
                    <td>
                      <strong>{r.grade}</strong>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'payments' && (
        <div className={styles.card} style={{ gridTemplateColumns: '1fr' }}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Concepto</th>
                <th>Periodo</th>
                <th>Total</th>
                <th>Estatus</th>
              </tr>
            </thead>
            <tbody>
              {student.payments.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center' }}>
                    Sin pagos
                  </td>
                </tr>
              ) : (
                student.payments.map((p) => (
                  <tr key={p.id}>
                    <td>{p.fee.name}</td>
                    <td>{p.period}</td>
                    <td>${p.total.toFixed(2)}</td>
                    <td>
                      <span className={p.status === 'PAID' ? styles.paid : styles.pending}>
                        {p.status === 'PAID' ? 'Pagado' : 'Pendiente'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
